import { colorBaseWhite } from '@issuu/silkscreen';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { HEADER_Z_INDEX } from '../../../constants';
import { GoogleAdsTemplate } from '../GoogleAdsTemplate';
import { InterstitialBottomPart } from './InterstitialBottomPart';
import type { PromotedDoc } from './InterstitialTopPart';
import { InterstitialTopPart } from './InterstitialTopPart';

const INITIAL_CLOSE_TIMEOUT = 6;

const AdsRoot = styled.div<{ showOverlay: boolean }>`
    width: ${({ showOverlay }) => (showOverlay ? '100%' : '1px')};
    height: ${({ showOverlay }) => (showOverlay ? '100%' : '1px')};
    display: flex;
    flex-direction: column;
    background-color: ${colorBaseWhite};
    position: fixed;
    top: 0;
    left: 0;
    z-index: ${HEADER_Z_INDEX};
`;

type PostMessage = {
    adId: string;
    countdownSecs: number;
    docname: string;
    headline: string;
    message: string;
    ownerUsername: string;
    pageNumber: number;
};

export const AdsInterstitial: React.FC = () => {
    const timeoutRef = useRef<number>(0);
    const [countdownSecs, setCountdownSecs] = useState<number>(INITIAL_CLOSE_TIMEOUT);
    const [autoClose, setAutoClose] = useState<boolean>(true);
    const [showOverlay, setShowOverlay] = useState<boolean>(false);

    const [promotedDoc, setPromotedDoc] = useState<PromotedDoc>({
        pageNumber: 1,
        adId: 'noCampaign',
        docname: '',
        headline: '',
        ownerName: '',
    });

    const closeInterstitial = () => {
        window.clearTimeout(timeoutRef.current);
        setShowOverlay(false);
    };

    const preventAutoClose = () => {
        setAutoClose(false);
    };

    const countDownOneSec = () => {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = window.setTimeout(() => {
            setCountdownSecs(countdownSecs - 1);
        }, 1000);
    };

    const onPostMessage = (event: MessageEvent<PostMessage>) => {
        // ensure message comes either from own origin or from Google DFP, discard otherwise
        if (
            ![location.origin, 'https://tpc.googlesyndication.com'].includes(event.origin) ||
            !event.data ||
            event.data.message !== 'eagles.dfp.interstitial.load'
        ) {
            return;
        }

        setPromotedDoc({
            ownerName: event.data.ownerUsername,
            docname: event.data.docname,
            headline: event.data.headline,
            adId: event.data.adId,
            pageNumber: event.data.pageNumber,
        });

        setCountdownSecs(event.data.countdownSecs);
        setShowOverlay(true);
    };

    useEffect(() => {
        countDownOneSec();
        window.addEventListener('message', onPostMessage);
        return () => {
            window.removeEventListener('message', onPostMessage);
        };
    }, []);

    useEffect(() => {
        if (countdownSecs > 0) {
            countDownOneSec();
        } else {
            autoClose && closeInterstitial();
        }
    }, [autoClose, countdownSecs]);

    return (
        <AdsRoot showOverlay={showOverlay}>
            {showOverlay ? (
                <>
                    <InterstitialTopPart
                        countdownSecs={countdownSecs}
                        onClose={closeInterstitial}
                        preventAutoClose={preventAutoClose}
                        promotedDoc={promotedDoc}
                    />
                    <InterstitialBottomPart
                        countdownSecs={countdownSecs}
                        onClose={closeInterstitial}
                    />
                </>
            ) : null}
            <GoogleAdsTemplate
                adSizes={[[1, 1]]}
                callbacks={{
                    onAdUnfilled: closeInterstitial,
                    onAdFailed: closeInterstitial,
                }}
                idSuffix="interstitial"
                network={'/30443627/DocPage_Interstitial_2'}
            />
        </AdsRoot>
    );
};
